/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';
import { msalConfig } from '@/config/authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

interface TokenData {
  accessToken: string;
  idToken: string;
  idTokenDecoded: any;
  groupIds: string[];
  userInfo: {
    email: string;
    gid: string;
  };
}

const useToken = () => {
  const { instance, accounts } = useMsal();

  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [, setLoading] = useState(true);
  const [, setError] = useState<any>(null);

  const fetchToken = useCallback(async () => {
    if (!accounts || accounts.length === 0) {
      setError(new Error('No authenticated account'));
      setLoading(false);
      return null;
    }

    const account = accounts[0];

    const request = {
      scopes: [`${msalConfig.auth.clientId}/.default`],
      account,
      forceRefresh: true,
    };

    try {
      const tokenResponse = await instance.acquireTokenSilent(request);

      const idTokenDecoded: any = jwtDecode(tokenResponse.idToken);

      const result: TokenData = {
        accessToken: tokenResponse.accessToken,
        idToken: tokenResponse.idToken,
        idTokenDecoded,
        groupIds: (tokenResponse.idTokenClaims as any).groups || [],
        userInfo: {
          email: (idTokenDecoded as any).email,
          gid: (idTokenDecoded as any).SCGID,
        },
      };

      setTokenData(result);
      setLoading(false);

      return result;
    } catch (err: any) {
      if (err instanceof InteractionRequiredAuthError) {
        instance.loginRedirect({
          scopes: [`${msalConfig.auth.clientId}/.default`],
        });
      } else {
        setError(err);
        setLoading(false);
      }

      return null;
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  const getToken = useCallback(async () => {
    if (tokenData?.accessToken) {
      return tokenData;
    }

    return fetchToken();
  }, [tokenData, fetchToken]);

  return getToken;
};

export default useToken;
